import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { useAuth } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Loader2, FileText, CheckCircle, Sparkles, AlertCircle, Upload, Pencil, X, Files } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (!value && value !== 0) return "";
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export const OCRUploadModal = ({ open, onOpenChange, onSuccess }) => {
  const { token } = useAuth();
  // Single file mode
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Batch mode
  const [files, setFiles] = useState([]);
  const [batchMode, setBatchMode] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, currentFile: '' });
  const [batchResults, setBatchResults] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    
    if (pdfFiles.length > 1) {
      // Multiple files - batch mode
      setBatchMode(true);
      setFiles(pdfFiles);
      setFile(null);
    } else if (pdfFiles.length === 1) {
      // Single file mode
      setBatchMode(false);
      setFile(pdfFiles[0]);
      setFiles([]);
    }
    
    // Reset states
    setExtractedData(null);
    setEditedData(null);
    setError(null);
    setSuccess(false);
    setEditMode(false);
    setBatchResults(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true // Allow multiple files
  });

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    if (newFiles.length === 1) {
      setBatchMode(false);
      setFile(newFiles[0]);
      setFiles([]);
    } else if (newFiles.length === 0) {
      setBatchMode(false);
    }
  };

  const handleExtract = async () => {
    if (!file) return;

    try {
      setExtracting(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/invoices/extract-preview`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      });
      
      setExtractedData(res.data.extracted_data);
      setEditedData(res.data.extracted_data);
      toast.success("Données extraites avec succès !");
    } catch (err) {
      console.error("OCR extraction error:", err);
      const errorMsg = err.response?.data?.detail || "Erreur lors de l'extraction OCR";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!editedData || !file) return;

    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/invoices/upload-with-ocr`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000
      });
      
      setSuccess(true);
      toast.success(`Facture ${res.data.invoice.numero} créée avec succès !`);
      onSuccess?.();
    } catch (err) {
      console.error("Save error:", err);
      const errorMsg = err.response?.data?.detail || "Erreur lors de la création";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleBatchUpload = async () => {
    if (files.length === 0) return;

    setBatchProcessing(true);
    setError(null);
    setBatchProgress({ current: 0, total: files.length, currentFile: files[0].name });
    
    const results = {
      total: files.length,
      successful: 0,
      failed: 0,
      created: [],
      errors: []
    };

    // Process files one by one to avoid timeout
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setBatchProgress({ current: i, total: files.length, currentFile: file.name });
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/invoices/upload-with-ocr`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 180000 // 180s (3 min) per file - OCR with retry can take longer
        });
        
        if (res.data.success) {
          results.successful++;
          results.created.push({
            filename: file.name,
            invoice: res.data.invoice
          });
        } else {
          results.failed++;
          results.errors.push({
            filename: file.name,
            error: res.data.error || "Erreur inconnue"
          });
        }
      } catch (err) {
        results.failed++;
        const errorMsg = err.response?.data?.detail || err.message || "Erreur lors de l'extraction";
        results.errors.push({
          filename: file.name,
          error: errorMsg
        });
      }
      
      // Small delay between files to respect Gemini rate limits
      if (i < files.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    setBatchProgress({ current: files.length, total: files.length, currentFile: '' });
    setBatchResults(results);
    setBatchProcessing(false);
    setError(null); // Clear any previous error
    
    if (results.successful > 0) {
      toast.success(`${results.successful} facture(s) créée(s)`);
      onSuccess?.();
    }
    if (results.failed > 0 && results.successful === 0) {
      toast.error(`${results.failed} facture(s) en erreur`);
    } else if (results.failed > 0) {
      toast.warning(`${results.failed} facture(s) n'ont pas pu être importées`);
    }
  };

  const handleClose = () => {
    if (!extracting && !saving && !batchProcessing) {
      setFile(null);
      setFiles([]);
      setExtractedData(null);
      setEditedData(null);
      setError(null);
      setSuccess(false);
      setEditMode(false);
      setBatchMode(false);
      setBatchResults(null);
      setBatchProgress({ current: 0, total: 0, currentFile: '' });
      onOpenChange(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Import avec OCR (Gemini)
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {batchMode 
              ? "Importez plusieurs factures PDF. Un délai de 3 secondes sera appliqué entre chaque extraction."
              : "Uploadez une ou plusieurs factures PDF et laissez l'IA extraire automatiquement les informations."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Success State - Single file */}
          {success && !batchMode && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Facture créée avec succès !
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                La facture a été ajoutée à votre liste.
              </p>
              <Button onClick={handleClose} className="btn-primary">
                Fermer
              </Button>
            </motion.div>
          )}

          {/* Batch Results */}
          {batchResults && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-4"
            >
              <div className="text-center py-4">
                {batchResults.successful > 0 ? (
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                ) : (
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                )}
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  Import terminé
                </h3>
                <p className="text-sm text-muted-foreground">
                  {batchResults.successful} facture(s) créée(s) sur {batchResults.total}
                </p>
              </div>

              {/* Results list */}
              <div className="max-h-60 overflow-y-auto space-y-2">
                {/* Successful imports */}
                {batchResults.created?.map((result, idx) => (
                  <div 
                    key={`success-${idx}`}
                    className="p-3 rounded-lg border bg-green-50 border-green-200"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm font-medium truncate flex-1">
                        {result.filename}
                      </span>
                    </div>
                    {result.invoice && (
                      <p className="text-xs text-green-700 mt-1 ml-6">
                        {result.invoice.numero} - {result.invoice.client}
                      </p>
                    )}
                  </div>
                ))}
                {/* Failed imports */}
                {batchResults.errors?.map((result, idx) => (
                  <div 
                    key={`error-${idx}`}
                    className="p-3 rounded-lg border bg-red-50 border-red-200"
                  >
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <span className="text-sm font-medium truncate flex-1">
                        {result.filename}
                      </span>
                    </div>
                    <p className="text-xs text-red-700 mt-1 ml-6">
                      {result.error}
                    </p>
                  </div>
                ))}
              </div>

              <Button onClick={handleClose} className="w-full">
                Fermer
              </Button>
            </motion.div>
          )}

          {/* Batch Processing */}
          {batchProcessing && (
            <div className="text-center py-8 space-y-4">
              <div className="relative">
                <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto" />
              </div>
              <div>
                <p className="text-lg font-semibold text-foreground">
                  Import en cours...
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {batchProgress.current} / {batchProgress.total} fichiers traités
                </p>
                {batchProgress.currentFile && (
                  <p className="text-xs text-purple-600 font-medium mt-2 truncate max-w-xs mx-auto">
                    En cours : {batchProgress.currentFile}
                  </p>
                )}
              </div>
              <Progress 
                value={(batchProgress.current / batchProgress.total) * 100} 
                className="w-full h-3"
              />
              <p className="text-xs text-muted-foreground">
                Extraction OCR de chaque facture en cours...
              </p>
            </div>
          )}

          {/* Dropzone - show when no file/files or no extraction */}
          {!success && !extractedData && !extracting && !batchResults && !batchProcessing && (
            <>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  isDragActive 
                    ? 'border-purple-500 bg-purple-50' 
                    : (file || files.length > 0)
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                }`}
                data-testid="ocr-dropzone"
              >
                <input {...getInputProps()} />
                <div className="space-y-3">
                  <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center ${
                    (file || files.length > 0) ? 'bg-green-100' : 'bg-purple-100'
                  }`}>
                    {(file || files.length > 0) ? (
                      files.length > 1 ? (
                        <Files className="w-7 h-7 text-green-600" />
                      ) : (
                        <FileText className="w-7 h-7 text-green-600" />
                      )
                    ) : (
                      <Upload className="w-7 h-7 text-purple-600" />
                    )}
                  </div>
                  {file && !batchMode ? (
                    <div>
                      <p className="text-sm font-medium text-foreground">{file.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Cliquez ou glissez pour changer de fichier
                      </p>
                    </div>
                  ) : files.length > 0 ? (
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {files.length} fichiers sélectionnés
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Cliquez ou glissez pour ajouter d'autres fichiers
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-foreground font-medium">
                        {isDragActive ? 'Déposez les PDF ici' : 'Glissez vos factures PDF'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        ou cliquez pour parcourir (un ou plusieurs fichiers)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Files list for batch mode */}
              {batchMode && files.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    Fichiers à importer ({files.length})
                  </p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {files.map((f, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                      >
                        <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        <span className="text-sm text-foreground truncate flex-1">{f.name}</span>
                        <button
                          onClick={() => removeFile(idx)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                    Un délai de 3 secondes sera appliqué entre chaque facture pour éviter la surcharge de l'API.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Action Buttons */}
          {!success && !extractedData && !extracting && !batchResults && !batchProcessing && (
            <>
              {/* Single file - Extract Button */}
              {file && !batchMode && (
                <Button
                  onClick={handleExtract}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12"
                  data-testid="extract-ocr-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Extraire les données avec l'IA
                </Button>
              )}

              {/* Batch mode - Upload Button */}
              {batchMode && files.length > 0 && (
                <Button
                  onClick={handleBatchUpload}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12"
                  data-testid="batch-upload-btn"
                >
                  <Files className="w-5 h-5 mr-2" />
                  Importer {files.length} factures
                </Button>
              )}
            </>
          )}

          {/* Loading State - Single file */}
          {extracting && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-4" />
              <p className="text-lg font-medium text-foreground">Analyse en cours...</p>
              <p className="text-sm text-muted-foreground mt-1">
                Gemini analyse votre facture PDF
              </p>
            </div>
          )}

          {/* Error State */}
          {error && !extracting && !batchProcessing && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-100">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Erreur d'extraction</p>
                  <p className="text-sm text-red-600 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Extracted Data Preview - Single file */}
          {!success && extractedData && !extracting && !batchMode && (
            <AnimatePresence mode="wait">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Header with Edit Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm font-medium text-foreground">Données extraites</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditMode(!editMode)}
                    className="text-xs"
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    {editMode ? 'Voir aperçu' : 'Modifier'}
                  </Button>
                </div>

                {/* Numero exists warning */}
                {extractedData.numero_exists && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800">
                      Une facture avec le numéro <strong>{editedData?.numero}</strong> existe déjà.
                      Modifiez le numéro avant de sauvegarder.
                    </p>
                  </div>
                )}

                {/* Edit Mode */}
                {editMode ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Numéro</Label>
                      <Input
                        value={editedData?.numero || ''}
                        onChange={(e) => handleInputChange('numero', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Client</Label>
                      <Input
                        value={editedData?.client || ''}
                        onChange={(e) => handleInputChange('client', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Date</Label>
                      <Input
                        type="date"
                        value={editedData?.date || ''}
                        onChange={(e) => handleInputChange('date', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Échéance</Label>
                      <Input
                        type="date"
                        value={editedData?.echeance || ''}
                        onChange={(e) => handleInputChange('echeance', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Total HT</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editedData?.total_ht || ''}
                        onChange={(e) => handleInputChange('total_ht', parseFloat(e.target.value))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">TVA</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editedData?.total_tva || ''}
                        onChange={(e) => handleInputChange('total_tva', parseFloat(e.target.value))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Total TTC</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editedData?.total_ttc || ''}
                        onChange={(e) => handleInputChange('total_ttc', parseFloat(e.target.value))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Type</Label>
                      <Input
                        value={editedData?.type || 'Facture'}
                        onChange={(e) => handleInputChange('type', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                ) : (
                  /* Preview Mode */
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Numéro</p>
                        <p className="text-sm font-mono font-medium text-blue-600">{editedData?.numero}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Type</p>
                        <Badge variant="outline" className="text-xs">
                          {editedData?.type || 'Facture'}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Client</p>
                      <p className="text-sm font-medium text-foreground">{editedData?.client}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Date</p>
                        <p className="text-sm text-foreground">{editedData?.date}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Échéance</p>
                        <p className="text-sm text-foreground">{editedData?.echeance || '-'}</p>
                      </div>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-xs uppercase text-muted-foreground mb-1">Total HT</p>
                          <p className="text-lg font-mono font-medium">{formatCurrency(editedData?.total_ht)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-muted-foreground mb-1">TVA</p>
                          <p className="text-lg font-mono text-muted-foreground">{formatCurrency(editedData?.total_tva)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-muted-foreground mb-1">Total TTC</p>
                          <p className="text-lg font-mono font-bold text-blue-600">{formatCurrency(editedData?.total_ttc)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Lines preview */}
                    {editedData?.lines?.length > 0 && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                          Lignes ({editedData.lines.length})
                        </p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {editedData.lines.slice(0, 5).map((line, idx) => (
                            <div key={idx} className="text-xs text-foreground truncate">
                              • {line.description}
                            </div>
                          ))}
                          {editedData.lines.length > 5 && (
                            <p className="text-xs text-muted-foreground">
                              ... et {editedData.lines.length - 5} autres lignes
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Save Button */}
                <Button
                  onClick={handleSave}
                  disabled={saving || extractedData.numero_exists}
                  className="w-full bg-green-600 hover:bg-green-700 text-white h-12"
                  data-testid="save-ocr-invoice-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Créer la facture
                    </>
                  )}
                </Button>
              </motion.div>
            </AnimatePresence>
          )}

          {/* Cancel Button */}
          {!success && !batchResults && (
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={extracting || saving || batchProcessing}
              className="w-full"
            >
              Annuler
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OCRUploadModal;
